/**
 * Leitura do que a pessoa logada pode abrir.
 *
 * Papel: expõe o `useAcesso`, que busca `GET /api/sessao` e devolve se a pessoa
 * é administradora e quais módulos lhe foram concedidos, em que nível. É daqui
 * que a tela inicial tira os cartões e que cada página tira o botão de salvar.
 *
 * Como usar: a decisão sobre o que mostrar deverá passar por `acessoAlcanca`, de
 * `@concierge/shared`, com o `data` deste hook — e nunca por uma leitura direta
 * de `permissoes`, que vem vazia para administrador e esconderia tudo de quem
 * administra. Esconder um botão é conveniência visual: a Function refaz a
 * verificação em `exigirPermissao` antes de tocar o banco.
 *
 * A consulta registra a entrada: a primeira chamada de uma pessoa cria a linha
 * dela em `usuarios`, sem permissão nenhuma, e as seguintes atualizam
 * `ultimoAcessoEm`. É por isso que alguém só aparece na tela de usuários depois
 * de entrar no Concierge ao menos uma vez, e é por isso que o cache fica com os
 * cinco minutos do `Providers` em vez de ser zerado — cada nova consulta é uma
 * escrita no banco.
 *
 * Uma permissão concedida por quem administra chega à outra pessoa quando ela
 * recarrega a página, ou quando os cinco minutos vencem e ela troca de tela.
 */
import { acessoSchema } from '@concierge/shared';
import { useQuery } from '@tanstack/react-query';

import { clienteApi } from '@/shared/api';

/** Chave de cache do acesso, para invalidação a partir de outras telas. */
export const CHAVE_ACESSO = ['acesso'] as const;

/**
 * Consulta o que a pessoa logada pode abrir.
 *
 * Quem chama precisa tratar `isPending`, `error` e `data`. Enquanto a consulta
 * não responde, nenhum módulo deverá ser desenhado como liberado; e erro deverá
 * ser tratado como acesso negado, não como acesso livre, para que uma API fora
 * do ar não transforme indisponibilidade em brecha na interface.
 *
 * @returns Resultado da consulta, com `data` no formato de `acessoSchema`.
 */
export function useAcesso() {
  return useQuery({
    queryKey: CHAVE_ACESSO,
    queryFn: ({ signal }) =>
      clienteApi.get('/sessao', { schema: acessoSchema, sinal: signal }),
  });
}
